/**
 * A schedule as the operator says it (Feature 81): every few minutes,
 * every hour, day, week, or month in the viewer's time, or a cron typed as
 * is; and the UTC cron the agent reads, which is still what is stored (D1).
 *
 * A time is converted with the offset in force at its next fire (D14), so
 * a weekday or a month's day moves when UTC falls on another day (D8, D9)
 * and an offset that is not whole hours moves the minute (D10). A day of
 * the month runs to 31, a month without it skipped; days 29 to 31 keep
 * only to times on the same UTC day (revision 1, D16, D17). A cron opens in
 * the choice that fires on the same minutes, or as typed (D11, D18).
 */
import { type CronField, type CronFields, isValidCron, nextSlots, parseCron } from './cron.js';

const WEEKDAY_WORDS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
/** The days of the week Monday first, as the builder and the readings list them. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
/** The steps every few minutes offers, each dividing the hour evenly (revision 1, D15). */
export const MINUTE_STEPS = [1, 5, 10, 15, 20, 30] as const;
/** The last day of the month the builder offers (revision 1, D16). */
export const MONTH_DAYS = 31;
// The last day every month has: a day past it is kept only on its own UTC day (D17).
const EVERY_MONTH_HAS = 28;
const DAY_MS = 86_400_000;
const MINUTE_MS = 60_000;
const MINUTES_PER_DAY = 24 * 60;
// A month's day recurs within this many local days (31st to 31st is at most 61), so the search ends.
const SEARCH_DAYS = 64;
// Three days in a row or more are written as a range, as `1-5`.
const RANGE_RUN = 3;
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
// One formatter per timezone: building an Intl formatter is the slow part.
const FORMATS = new Map<string, Intl.DateTimeFormat>();

/** What a schedule says: its choice and the fields that choice needs, times as `HH:MM` local. */
export type Schedule =
  | { every: 'minutes'; step: number }
  | { every: 'hour'; minute: number }
  | { every: 'day'; time: string }
  | { every: 'week'; days: readonly number[]; time: string }
  | { every: 'month'; day: number; time: string }
  | { every: 'custom'; cron: string };

/** The six choices. */
export type Every = Schedule['every'];

/** The cron a schedule makes, or why it makes none. */
export type ScheduleCron =
  | { cron: string }
  | { cron: null; reason: 'no_day' | 'no_time' | 'invalid' }
  | {
      cron: null;
      reason: 'month_day_leaves';
      /** The nearest local time that keeps the day, and on which side of it to pick. */
      nearest: string;
      side: 'later' | 'earlier';
    };

/** A local date and time read off a clock in a timezone. */
interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** A schedule's next fire: the local day it falls on, as a UTC midnight, and its instant. */
interface Fire {
  day: Date;
  at: Date;
}

/** `n` modulo `m`, never negative. */
function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** Two digits. */
function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Minutes after midnight as `HH:MM`. */
function timeText(minutes: number): string {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** `HH:MM` as minutes after midnight, or null when it is not a time. */
function minutesOf(time: string): number | null {
  const match = TIME.exec(time);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/** The formatter that reads a clock in `timeZone`, built once per timezone. */
function formatIn(timeZone: string): Intl.DateTimeFormat {
  let format = FORMATS.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    });
    FORMATS.set(timeZone, format);
  }
  return format;
}

/** What a clock in `timeZone` reads at `at`. */
function wallClock(at: Date, timeZone: string): WallClock {
  const parts = formatIn(timeZone).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
  };
}

/** How many minutes `timeZone` is ahead of UTC at `at`. */
function offsetMinutes(at: Date, timeZone: string): number {
  const w = wallClock(at, timeZone);
  const local = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute);
  return Math.round((local - Math.floor(at.getTime() / MINUTE_MS) * MINUTE_MS) / MINUTE_MS);
}

/** The UTC midnight of an instant's UTC date. */
function utcDay(at: Date): number {
  return Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate());
}

/** The instant a local day (a UTC midnight standing for it) reads `minutes` after midnight. */
function instantOf(day: Date, minutes: number, timeZone: string): Date {
  const guess = day.getTime() + minutes * MINUTE_MS;
  const first = guess - offsetMinutes(new Date(guess), timeZone) * MINUTE_MS;
  // Once more with the offset at the first answer, which differs only across a change.
  return new Date(guess - offsetMinutes(new Date(first), timeZone) * MINUTE_MS);
}

/** The first local day from today `matches` accepts whose time is still to come. */
function nextFire(
  minutes: number,
  timeZone: string,
  now: Date,
  matches: (day: Date) => boolean,
): Fire | null {
  const today = wallClock(now, timeZone);
  for (let i = 0; i < SEARCH_DAYS; i += 1) {
    const day = new Date(Date.UTC(today.year, today.month - 1, today.day + i));
    if (!matches(day)) continue;
    const at = instantOf(day, minutes, timeZone);
    if (at > now) return { day, at };
  }
  return null;
}

/** The local days a time-of-day choice fires on. */
function matcher(schedule: Schedule): (day: Date) => boolean {
  if (schedule.every === 'week') return (day) => schedule.days.includes(day.getUTCDay());
  if (schedule.every === 'month') return (day) => day.getUTCDate() === schedule.day;
  return () => true;
}

/** Weekdays as a cron field: in order, three in a row or more as a range. */
function dayList(days: readonly number[]): string {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const parts: string[] = [];
  let start = 0;
  sorted.forEach((day, i) => {
    if (sorted[i + 1] === day + 1) return;
    const run = sorted.slice(start, i + 1);
    parts.push(run.length >= RANGE_RUN ? `${run[0]}-${day}` : run.join(','));
    start = i + 1;
  });
  return parts.join(',');
}

/** The cron of a choice with a time of day, from its next fire. */
function timedCron(
  schedule: Extract<Schedule, { time: string }>,
  minutes: number,
  timeZone: string,
  now: Date,
): ScheduleCron {
  const fire = nextFire(minutes, timeZone, now, matcher(schedule));
  if (fire === null) return { cron: null, reason: 'invalid' };
  const at = `${fire.at.getUTCMinutes()} ${fire.at.getUTCHours()}`;
  // -1 when UTC is still on the day before, +1 when it is already on the next.
  const shift = Math.round((utcDay(fire.at) - fire.day.getTime()) / DAY_MS);
  if (schedule.every === 'week' && new Set(schedule.days).size < WEEK_ORDER.length) {
    return { cron: `${at} * * ${dayList(schedule.days.map((d) => mod(d + shift, 7)))}` };
  }
  if (schedule.every === 'month') {
    const day = schedule.day + shift;
    // On its own UTC day any day stays; moved, both days must be ones every month has.
    const kept =
      shift === 0 || (schedule.day <= EVERY_MONTH_HAS && day >= 1 && day <= EVERY_MONTH_HAS);
    if (kept) return { cron: `${at} ${day} * *` };
    const offset = offsetMinutes(fire.at, timeZone);
    // Ahead of UTC a day starts at UTC's midnight; behind it, it ends at UTC's last minute.
    return shift < 0
      ? { cron: null, reason: 'month_day_leaves', nearest: timeText(offset), side: 'later' }
      : {
          cron: null,
          reason: 'month_day_leaves',
          nearest: timeText(MINUTES_PER_DAY - 1 + offset),
          side: 'earlier',
        };
  }
  return { cron: `${at} * * *` };
}

/** A field's single value, or null. */
function single(field: CronField): number | null {
  return field.values.size === 1 ? ([...field.values][0] ?? null) : null;
}

/** The step of minutes even from :00 a minute field fires on, when it is one offered; else null. */
function minuteStep(field: CronField): number | null {
  const values = [...field.values].sort((a, b) => a - b);
  const step = (values[1] ?? 0) - (values[0] ?? 0);
  const even = values[0] === 0 && values.length * step === 60;
  const offered = (MINUTE_STEPS as readonly number[]).includes(step);
  return even && offered && values.every((v, i) => v === i * step) ? step : null;
}

/** The choice that may make these fields, read off their next slot; null when none can. */
function candidateOf(f: CronFields, slot: Date, timeZone: string, now: Date): Schedule | null {
  if (!f.month.any) return null;
  const everyWeekday = f.weekday.values.size === WEEK_ORDER.length;
  const step = minuteStep(f.minute);
  if (step !== null && f.hour.any && f.day.any && everyWeekday) return { every: 'minutes', step };
  const minute = single(f.minute);
  if (minute === null) return null;
  if (f.hour.any && f.day.any && everyWeekday) {
    return { every: 'hour', minute: mod(minute + offsetMinutes(now, timeZone), 60) };
  }
  if (single(f.hour) === null) return null;
  const w = wallClock(slot, timeZone);
  const time = `${pad(w.hour)}:${pad(w.minute)}`;
  // How many days the local date is ahead of the UTC date at the slot.
  const shift = Math.round((Date.UTC(w.year, w.month - 1, w.day) - utcDay(slot)) / DAY_MS);
  if (f.day.any && everyWeekday) return { every: 'day', time };
  if (f.day.any) {
    const days = [...f.weekday.values].map((d) => mod(d + shift, 7)).sort((a, b) => a - b);
    return { every: 'week', days, time };
  }
  const day = single(f.day);
  if (day === null || !f.weekday.any) return null;
  const local = day + shift;
  return local >= 1 && local <= MONTH_DAYS ? { every: 'month', day: local, time } : null;
}

/** The fields as compared: the values, and whether the two day fields are either one. */
function canonical(f: CronFields): string {
  const list = (field: CronField) => [...field.values].sort((a, b) => a - b).join(',');
  const either = !f.day.any && !f.weekday.any;
  return [list(f.minute), list(f.hour), list(f.day), list(f.month), list(f.weekday), either].join(
    ' ',
  );
}

/** Whether two crons fire on the same minutes. */
function sameFires(a: string, b: string): boolean {
  const left = parseCron(a);
  const right = parseCron(b);
  return left !== null && right !== null && canonical(left) === canonical(right);
}

/** "Every day", "Every weekday", "Every weekend", or the days by name, Monday first. */
function daysWord(days: readonly number[]): string {
  const set = new Set(days);
  if (set.size === WEEK_ORDER.length) return 'Every day';
  const key = [...set].sort((a, b) => a - b).join('');
  if (key === '12345') return 'Every weekday';
  if (key === '06') return 'Every weekend';
  return WEEK_ORDER.filter((d) => set.has(d))
    .map((d) => WEEKDAY_WORDS[d])
    .join(', ');
}

/** Every minute or every few minutes, the readings a custom cron keeps; null for any other. */
function stepWords(cron: string): string | null {
  const p = parseCron(cron);
  if (p === null || !p.hour.any || !p.day.any || !p.month.any || !p.weekday.any) return null;
  if (p.minute.any) return 'Every minute';
  const steps = [...p.minute.values].sort((a, b) => a - b);
  const gap = (steps[1] ?? 0) - (steps[0] ?? 0);
  const even = steps[0] === 0 && steps.every((v, i) => v === i * gap) && 60 % gap === 0;
  return even && gap > 1 ? `Every ${gap} minutes` : null;
}

/** The UTC cron a schedule makes in `timeZone`, its time placed at its next fire after `now`. */
export function scheduleCron(schedule: Schedule, timeZone: string, now: Date): ScheduleCron {
  if (schedule.every === 'custom') {
    const cron = schedule.cron.trim();
    return isValidCron(cron) ? { cron } : { cron: null, reason: 'invalid' };
  }
  if (schedule.every === 'minutes') {
    const { step } = schedule;
    if (!(MINUTE_STEPS as readonly number[]).includes(step))
      return { cron: null, reason: 'invalid' };
    return { cron: step === 1 ? '* * * * *' : `*/${step} * * * *` };
  }
  if (schedule.every === 'hour') {
    const { minute } = schedule;
    if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
      return { cron: null, reason: 'no_time' };
    }
    return { cron: `${mod(minute - offsetMinutes(now, timeZone), 60)} * * * *` };
  }
  const minutes = minutesOf(schedule.time);
  if (minutes === null) return { cron: null, reason: 'no_time' };
  if (schedule.every === 'week' && schedule.days.length === 0) {
    return { cron: null, reason: 'no_day' };
  }
  if (schedule.every === 'month' && (schedule.day < 1 || schedule.day > MONTH_DAYS)) {
    return { cron: null, reason: 'invalid' };
  }
  return timedCron(schedule, minutes, timeZone, now);
}

/** The choice that makes `cron` in `timeZone`, or Custom holding it as typed. */
export function scheduleOf(cron: string, timeZone: string, now: Date): Schedule {
  const custom: Schedule = { every: 'custom', cron };
  const fields = parseCron(cron);
  const slot = nextSlots(cron, now, 1)[0];
  if (fields === null || slot === undefined) return custom;
  const candidate = candidateOf(fields, slot, timeZone, now);
  if (candidate === null) return custom;
  // Only a choice that makes the same fires opens as itself; never a near one.
  const made = scheduleCron(candidate, timeZone, now);
  return made.cron !== null && sameFires(made.cron, cron) ? candidate : custom;
}

/** A schedule in words, in the time it was said in; null for a custom cron with no reading. */
export function scheduleWords(schedule: Schedule): string | null {
  switch (schedule.every) {
    case 'minutes':
      return schedule.step === 1 ? 'Every minute' : `Every ${schedule.step} minutes`;
    case 'hour':
      return `Every hour at :${pad(schedule.minute)}`;
    case 'day':
      return `Every day at ${schedule.time}`;
    case 'week':
      return `${daysWord(schedule.days)} at ${schedule.time}`;
    case 'month':
      return `Day ${schedule.day} of every month at ${schedule.time}`;
    case 'custom':
      return stepWords(schedule.cron);
  }
}

/**
 * The cron in words in the viewer's timezone, named after a time of day,
 * or null for a shape not read: never a guess.
 */
export function cronReading(cron: string, timeZone: string, now: Date = new Date()): string | null {
  const schedule = scheduleOf(cron, timeZone, now);
  const words = scheduleWords(schedule);
  // Only a time of day is named with its zone; a step of minutes and the hour's minute stand alone.
  const timeOfDay =
    schedule.every === 'day' || schedule.every === 'week' || schedule.every === 'month';
  return words !== null && timeOfDay ? `${words}, ${timeZone}` : words;
}

/** What a monthly schedule on day 29 to 31 skips, said under the sentence (D16); null for any other day. */
export function monthsSkipped(day: number): string | null {
  if (day === 29) return 'Skips February, except in a leap year.';
  if (day === 30) return 'Skips February.';
  if (day === 31) {
    return 'Skips the months without a 31st: February, April, June, September, November.';
  }
  return null;
}
